import React, { useState, useCallback } from 'react';
import { Search, Package, Filter, User, CheckCircle, MapPin, ChevronRight, ChevronDown } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { putAwayService } from '../../../../services/putAwayService';
import { StcIntRelOrderDto } from '../../../../types/putaway';
import { PreviewPutAwayModal } from './PreviewPutAwayModal';
import { PutAwayCard } from './PutAwayCard';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface ModernPutAwayListProps {
  putAwayOrders: StcIntRelOrderDto[];
  onSelectPutAway: (putAwayOrder: StcIntRelOrderDto) => Promise<void>;
  onNextPutAway: () => Promise<void>;
  onRefreshOrders: () => void;
  onBack?: () => void;
}

export const ModernPutAwayList: React.FC<ModernPutAwayListProps> = ({
  putAwayOrders,
  onSelectPutAway,
  onNextPutAway,
  onRefreshOrders,
  onBack
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingNextPutAway, setLoadingNextPutAway] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewStcOrderNo, setPreviewStcOrderNo] = useState<number | null>(null);
  const [isMyPutAwayExpanded, setIsMyPutAwayExpanded] = useState(true);
  const [isAvailablePutAwayExpanded, setIsAvailablePutAwayExpanded] = useState(true);

  // Optimized callback functions with useCallback to prevent unnecessary re-renders
  const handleLockPutAwayOrder = useCallback(async (stcOrderNo: number) => {
    try {
      await putAwayService.lockPutAwayOrder(stcOrderNo);
      // Oppdater listen for å vise den nye låsstatusen
      onRefreshOrders();
    } catch (err) {
      console.error('Error locking put-away order:', err);
      alert(t('warehouse.modules.putaway.errors.lockOrderFailed'));
    }
  }, [onRefreshOrders]);

  const handleUnlockPutAwayOrder = useCallback(async (stcOrderNo: number) => {
    try {
      await putAwayService.unlockPutAwayOrder(stcOrderNo);
      // Oppdater listen for å vise den nye låsstatusen
      onRefreshOrders();
    } catch (err) {
      console.error('Error unlocking put-away order:', err);
      alert(t('warehouse.modules.putaway.errors.unlockOrderFailed'));
    }
  }, [onRefreshOrders]);

  const handleStartPutAway = useCallback(async (putAwayOrder: StcIntRelOrderDto) => {
    try {
      await onSelectPutAway(putAwayOrder);
    } catch (err) {
      console.error('Error starting put-away:', err);
      // Error is already handled in onSelectPutAway
    }
  }, [onSelectPutAway]);

  const handleNextPutAway = async () => {
    try {
      setLoadingNextPutAway(true);
      await onNextPutAway();
    } catch (err) {
      console.error('Error starting next put-away:', err);
      // Error is already handled in onNextPutAway
    } finally {
      setLoadingNextPutAway(false);
    }
  };

  const handlePreviewPutAway = useCallback((stcOrderNo: number) => {
    setPreviewStcOrderNo(stcOrderNo);
    setShowPreviewModal(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setShowPreviewModal(false);
    setPreviewStcOrderNo(null);
  }, []);

  // Del opp utplasseringsordrer basert på LockedBy-status
  const myPutAwayOrders = putAwayOrders.filter(order => 
    order.lockedBy && order.lockedBy.trim() !== ''
  );
  
  const availablePutAwayOrders = putAwayOrders.filter(order => 
    !order.lockedBy || order.lockedBy.trim() === ''
  );

  // Filtrer basert på søkebegrep
  const filteredMyOrders = myPutAwayOrders.filter(order => 
    order.description1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.companyNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.stcOrderNo.toString().includes(searchTerm)
  );

  const filteredAvailableOrders = availablePutAwayOrders.filter(order => 
    order.description1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.companyNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.stcOrderNo.toString().includes(searchTerm)
  );

  // Render optimized put-away sections with collapsible support
  const renderPutAwaySection = (
    title: string,
    orders: StcIntRelOrderDto[],
    isOwnedSection: boolean,
    icon: React.ReactNode,
    isCollapsible: boolean = false,
    isExpanded: boolean = true,
    onToggleExpand?: () => void
  ) => {
    if (orders.length === 0) return null;

    return (
      <div className="mb-4 sm:mb-8">
        <div
          className={`text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 ${
            isCollapsible ? 'cursor-pointer hover:text-green-600 transition-colors' : ''
          }`}
          onClick={isCollapsible ? onToggleExpand : undefined}
        >
          {isCollapsible && (
            isExpanded ? (
              <ChevronDown className="text-gray-600 flex-shrink-0" size={24} />
            ) : (
              <ChevronRight className="text-gray-600 flex-shrink-0" size={24} />
            )
          )}
          {icon}
          <span className="truncate">{title} ({orders.length})</span>
        </div>
        {isExpanded && (
          <div className="space-y-2 sm:space-y-3">
            {orders.map((order) => (
              <PutAwayCard
                key={order.stcOrderNo}
                order={order}
                isOwned={isOwnedSection}
                onStartPutAway={handleStartPutAway}
                onLockOrder={handleLockPutAwayOrder}
                onUnlockOrder={handleUnlockPutAwayOrder}
                onPreviewOrder={handlePreviewPutAway}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-3 sm:mb-6">
          {/* Header with title and search field - More compact on mobile */}
          <div className="space-y-3 sm:space-y-0 sm:flex sm:items-center sm:gap-4 mb-3 sm:mb-4">
            <div className="flex items-center gap-3 flex-shrink-0">
              <BackButton onClick={onBack || (() => {})} />
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                <span className="hidden sm:inline">{t('warehouse.modules.putaway.title')}</span>
                <span className="sm:hidden">{t('warehouse.modules.putaway.shortName')}</span>
              </h1>
            </div>
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder={t('warehouse.modules.putaway.searchPlaceholder')}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          {/* Action buttons - Full width on mobile, compact on desktop */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
            <button
              onClick={handleNextPutAway}
              disabled={loadingNextPutAway}
              className="flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 min-h-[44px] text-sm sm:text-base"
            >
              {loadingNextPutAway ? (
                <>
                  <div className="w-4 h-4 animate-spin border-2 border-white border-t-transparent rounded-full" />
                  <span>{t('warehouse.modules.putaway.actions.starting')}</span>
                </>
              ) : (
                <>
                  <MapPin size={16} />
                  <span>{t('warehouse.modules.putaway.actions.nextPutaway')}</span>
                </>
              )}
            </button>

            <button
              onClick={onRefreshOrders}
              className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors min-h-[44px] text-sm sm:text-base"
            >
              <Filter size={16} />
              <span>{t('common.buttons.refresh')}</span>
            </button>
          </div>
        </div>

        {/* Mine utplasseringer-seksjonen */}
        {renderPutAwaySection(
          t('warehouse.modules.putaway.sections.myPutaways'),
          filteredMyOrders,
          true,
          <User className="text-green-600 flex-shrink-0" size={20} />,
          true,
          isMyPutAwayExpanded,
          () => setIsMyPutAwayExpanded(!isMyPutAwayExpanded)
        )}

        {/* Tilgjengelige utplasseringer-seksjonen */}
        {renderPutAwaySection(
          t('warehouse.modules.putaway.sections.availablePutaways'),
          filteredAvailableOrders,
          false,
          <MapPin className="text-green-600 flex-shrink-0" size={20} />,
          true,
          isAvailablePutAwayExpanded,
          () => setIsAvailablePutAwayExpanded(!isAvailablePutAwayExpanded)
        )}

        {filteredAvailableOrders.length === 0 && filteredMyOrders.length === 0 && (
          <div className="text-center py-8 sm:py-12 text-gray-500">
            <MapPin size={40} className="sm:w-12 sm:h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-sm sm:text-base">{t('warehouse.modules.putaway.messages.noOrdersFound')}</p>
            {searchTerm && (
              <p className="text-xs sm:text-sm text-gray-400 mt-1">
                {t('warehouse.modules.putaway.messages.changeSearchOrRefresh')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <PreviewPutAwayModal
        isOpen={showPreviewModal}
        stcOrderNo={previewStcOrderNo}
        onClose={handleClosePreview}
      />
    </div>
  );
};
